import { db } from '../firebase';
import { createCallable } from '../firebaseFunctions';
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from 'firebase/firestore';

/**
 * How long the browser waits for a chat turn to report back: the `chat`
 * function's own limit (`timeoutSeconds: 540`, functions/features/chat/index.js)
 * plus 30 s for its last progress write. The callable returns at once and the
 * turn runs on; if the function dies, no snapshot ever arrives, and without
 * this the chat stayed "thinking" until a reload.
 */
export const CHAT_TURN_TIMEOUT_MS = (540 + 30) * 1000;

/**
 * Chat Service
 * Handles communication with Firebase Functions chat API
 */
class ChatService {
  constructor() {
    this.chatFunction = createCallable('chat');
    this.getChatProgressFunction = createCallable('getChatProgress');
    this.clearHistoryFunction = createCallable('clearChatHistory');
    this.activeSubscriptions = new Map();
    this.turnTimers = new Map();
    this.conversationHistory = [];
  }

  /**
   * Send a message to the chat API
   * @param {string} message - User's message
   * @param {Object} context - Optional context (structureId, roomId)
   * @returns {Promise<Object>} Response with taskId
   */
  async sendMessage(message, context = {}) {
    try {
      // Limit history to last 6 messages (3 exchanges) to reduce token usage
      const limitedHistory = this.conversationHistory.slice(-6);

      // Just pass context through directly - it's already clean from ChatContext
      const result = await this.chatFunction({
        message,
        history: limitedHistory,
        ...context,
      });

      // Add user message to history
      this.conversationHistory.push({
        role: 'user',
        content: message,
      });

      // Store context from the response if available
      if (result.data?.context) {
        this.lastContext = result.data.context;
      }

      // Keep conversation history limited to avoid growing too large
      if (this.conversationHistory.length > 20) {
        this.conversationHistory = this.conversationHistory.slice(-20);
      }

      return result.data;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Subscribe to progress updates for a task
   * @param {string} taskId - Task ID to monitor
   * @param {Function} onProgress - Callback for progress updates
   * @param {Function} onComplete - Callback when task completes
   * @param {Function} onError - Callback for errors
   * @returns {Function} Unsubscribe function
   */
  subscribeToProgress(taskId, onProgress, onComplete, onError) {
    // Unsubscribe from any existing subscription for this task
    this.unsubscribeFromProgress(taskId);

    const progressRef = doc(db, 'chat_progress', taskId);

    const unsubscribe = onSnapshot(
      progressRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          this.clearTurnTimer(taskId);
          onError(new Error('Progress document not found'));
          return;
        }

        const data = snapshot.data();

        // Call progress callback
        onProgress(data);

        if (data.status === 'complete' || data.status === 'error') {
          this.clearTurnTimer(taskId);
        }

        // Check if complete
        if (data.status === 'complete') {
          // Add assistant message to history
          if (data.result?.message) {
            this.conversationHistory.push({
              role: 'assistant',
              content: data.result.message,
            });

            // Extract and preserve IDs from the response
            // Context extraction no longer needed - handled by ChatContext
          }

          // Call complete callback with result, UI actions, and activity feed
          onComplete(data.result, data.uiActions || [], data.activity || []);

          // Auto-unsubscribe after completion
          setTimeout(() => this.unsubscribeFromProgress(taskId), 100);
        } else if (data.status === 'error') {
          onError(new Error(data.error || 'Task failed'));

          // Auto-unsubscribe after error
          setTimeout(() => this.unsubscribeFromProgress(taskId), 100);
        }
      },
      (error) => {
        console.error('Error subscribing to progress:', error);
        this.clearTurnTimer(taskId);
        onError(error);
      }
    );

    // Store subscription
    this.activeSubscriptions.set(taskId, unsubscribe);
    this.turnTimers.set(
      taskId,
      setTimeout(() => {
        this.unsubscribeFromProgress(taskId);
        onError(
          new Error('The assistant took too long and stopped. Try again.')
        );
      }, CHAT_TURN_TIMEOUT_MS)
    );

    return unsubscribe;
  }

  /**
   * Unsubscribe from progress updates
   * @param {string} taskId - Task ID to stop monitoring
   */
  unsubscribeFromProgress(taskId) {
    this.clearTurnTimer(taskId);
    const unsubscribe = this.activeSubscriptions.get(taskId);
    if (unsubscribe) {
      unsubscribe();
      this.activeSubscriptions.delete(taskId);
    }
  }

  /**
   * Get progress for a specific task
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Progress data
   */
  async getProgress(taskId) {
    try {
      const result = await this.getChatProgressFunction({ taskId });
      return result.data.progress;
    } catch (error) {
      console.error('Error getting progress:', error);
      throw this.formatError(error);
    }
  }

  // Context extraction method removed - no longer needed
  // Context is now managed entirely by ChatContext and passed through directly

  /**
   * Clear chat history
   * @returns {Promise<Object>} Clear result
   */
  clearTurnTimer(taskId) {
    const timer = this.turnTimers.get(taskId);
    if (timer) {
      clearTimeout(timer);
      this.turnTimers.delete(taskId);
    }
  }

  /** Forget the conversation the model is sent; no server call. */
  resetConversation() {
    this.conversationHistory = [];
  }

  async clearHistory() {
    try {
      const result = await this.clearHistoryFunction();
      this.conversationHistory = [];
      return result.data;
    } catch (error) {
      console.error('Error clearing history:', error);
      throw this.formatError(error);
    }
  }

  /**
   * Get recent chat history from Firestore
   * @param {number} limitCount - Number of recent messages
   * @returns {Promise<Array>} Chat messages
   */
  async getRecentHistory(limitCount = 10) {
    try {
      const q = query(
        collection(db, 'chat_progress'),
        where('status', '==', 'complete'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      const messages = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.result?.message) {
          messages.push({
            id: doc.id,
            message: data.result.message,
            timestamp: data.createdAt,
            uiActions: data.uiActions || [],
          });
        }
      });

      return messages.reverse();
    } catch (error) {
      console.error('Error getting chat history:', error);
      return [];
    }
  }

  /**
   * Set conversation history (for resuming conversations)
   * @param {Array} history - Conversation history
   */
  setConversationHistory(history) {
    this.conversationHistory = history;
  }

  /**
   * Get current conversation history
   * @returns {Array} Conversation history
   */
  getConversationHistory() {
    return this.conversationHistory;
  }

  /**
   * Format error for display
   * @param {Error} error - Error to format
   * @returns {Error} Formatted error
   */
  formatError(error) {
    if (error.code === 'unauthenticated') {
      return new Error('Please sign in to use chat');
    }
    if (error.code === 'failed-precondition') {
      return new Error('Chat service is not configured properly');
    }
    if (error.code === 'invalid-argument') {
      return new Error('Invalid message format');
    }
    return new Error(error.message || 'Chat service error');
  }

  /**
   * Cleanup all subscriptions
   */
  cleanup() {
    this.activeSubscriptions.forEach((unsubscribe) => unsubscribe());
    this.activeSubscriptions.clear();
  }
}

// Create singleton instance
const chatService = new ChatService();

export default chatService;
